/**
 * EntityDetailModal.tsx
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Reusable full-width modal container for entity details with
 *          two-column layout (read-only left, editable right).
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Save, History, ExternalLink } from 'lucide-react';
import { Modal, Button, Alert, Spinner } from '@/components/ui';

export interface EntityDetailModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title (entity name) */
  title: string;
  /** Entity type for display and audit */
  entityType: 'storage_account' | 'capacity' | 'workspace' | 'lakehouse' | 'warehouse';
  /** Entity ID for audit trail */
  entityId: string;
  /** Whether form has unsaved changes */
  isDirty?: boolean;
  /** Whether form is currently saving */
  isSaving?: boolean;
  /** Callback when save is triggered */
  onSave?: () => void;
  /** Error message to display */
  error?: string | null;
  /** Retry handler for the error (e.g. refetch); adds a Retry action to the error banner */
  onRetry?: () => void;
  /** Read-only fields content (left column) */
  readOnlyContent: React.ReactNode;
  /** Editable fields content (right column) */
  editableContent: React.ReactNode;
  /** Change history content (collapsible section) */
  changeHistoryContent?: React.ReactNode;
  /** Whether to show change history section */
  showChangeHistory?: boolean;
  /** URL to full detail page with more options (storage, backups, etc.) */
  detailPageUrl?: string;
}

/**
 * EntityDetailModal - Full-width modal for viewing and editing entity details
 *
 * Layout:
 * - 900px width modal
 * - Two-column layout: left (read-only, grey) | right (editable, white)
 * - Collapsible change history section at bottom
 */
export const EntityDetailModal: React.FC<EntityDetailModalProps> = ({
  open,
  onClose,
  title,
  entityType,
  entityId,
  isDirty = false,
  isSaving = false,
  onSave,
  error,
  onRetry,
  readOnlyContent,
  editableContent,
  changeHistoryContent,
  showChangeHistory = false,
  detailPageUrl,
}) => {
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirmClose = useCallback(() => {
    setConfirmClose(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setConfirmClose(false);
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
    }
  }, [onSave]);

  const toggleHistory = useCallback(() => {
    setHistoryExpanded((prev) => !prev);
  }, []);

  const getEntityTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      storage_account: 'Storage Account',
      capacity: 'Capacity',
      workspace: 'Workspace',
      lakehouse: 'Lakehouse',
      warehouse: 'Warehouse',
    };
    return labels[type] || type;
  };

  const titleNode = (
    <div className="flex items-center gap-4">
      <div className="flex items-baseline gap-3">
        <span className="text-lg font-semibold text-[var(--color-text-primary)]">
          {title}
        </span>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {getEntityTypeLabel(entityType)}
        </span>
      </div>
      {detailPageUrl && (
        <Link
          to={detailPageUrl}
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-accent)] px-3 py-1 text-sm text-[var(--color-accent)] hover:bg-[var(--color-bg-subtle)]"
        >
          Full Details
          <ExternalLink className="h-4 w-4" />
        </Link>
      )}
    </div>
  );

  const footerNode = (
    <div className="flex w-full items-center">
      <span className="flex-1 text-xs text-[var(--color-text-secondary)]">
        ID: {entityId}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          leftIcon={isSaving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={open}
        onClose={handleClose}
        title={titleNode}
        footer={footerNode}
        size="lg"
      >
        {/* Content */}
        {error && (
          <Alert
            variant="error"
            className="m-2"
            action={
              onRetry ? (
                <Button variant="secondary" size="sm" onClick={onRetry}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}

        <div className="flex min-h-[400px] flex-col md:flex-row">
          {/* Left Column - Read-Only Fields (Subtle Background) */}
          <div className="flex-1 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-6 md:border-b-0 md:border-r">
            <p className="mb-4 text-sm font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
              Azure-Controlled Properties
            </p>
            {readOnlyContent}
          </div>

          {/* Right Column - Editable Fields (Card Background) */}
          <div className="flex-1 bg-[var(--color-bg-card)] p-6">
            <p className="mb-4 text-sm font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
              Control Plane Configuration
            </p>
            {editableContent}
          </div>
        </div>

        {/* Change History Section (Collapsible) */}
        {showChangeHistory && changeHistoryContent && (
          <>
            <hr className="border-[var(--color-border)]" />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleHistory}
                leftIcon={<History className="h-4 w-4" />}
                className={historyExpanded ? 'mb-2' : ''}
              >
                {historyExpanded ? 'Hide Change History' : 'Show Change History'}
              </Button>
              {historyExpanded && <div className="mt-1">{changeHistoryContent}</div>}
            </div>
          </>
        )}
      </Modal>

      {/* Unsaved Changes Confirmation Dialog */}
      <Modal
        isOpen={confirmClose}
        onClose={handleCancelClose}
        title="Unsaved Changes"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={handleCancelClose}>
              Keep Editing
            </Button>
            <Button variant="danger" onClick={handleConfirmClose}>
              Discard Changes
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-primary)]">
          You have unsaved changes. Are you sure you want to close without saving?
        </p>
      </Modal>
    </>
  );
};

export default EntityDetailModal;
